import {
  Crosshair2Icon,
  GlobeIcon,
  Pencil1Icon,
  PinTopIcon,
  ReloadIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import { MapDrawer } from "src/components/RouteMap";
import SessionEditDrawer from "src/components/SessionEditDrawer";
import { FriendTag } from "src/components/active-friends-chips/FriendChip";
import DrinkLog from "src/components/drink-log/DrinkLog";
import { Button } from "src/components/ui/button";
import { Separator } from "src/components/ui/separator";
import getLogsFromAlcoholData from "src/lib/getLogsFromAlcoholData";
import { useApiRequest } from "src/lib/hooks/useApiRequest";
import useSWR from "swr";
import { Session as SessionType } from "./Sessions";

const fetcher = (token) => async (url) => {
  const res = await fetch(url, { headers: { Authorization: token } });

  if (!res.ok) {
    throw await res.json();
  }

  return res.json();
};

export default function Session() {
  const sessionId = useParams().sessionId;

  const [clickedDelete, setClickedDelete] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);

  const user = useSelector((state: any) => state.user);

  const { data, error, isLoading, mutate } = useSWR<SessionType>(
    `${process.env.REACT_APP_API_URL}/sessions/${encodeURIComponent(
      sessionId
    )}`,
    fetcher(user.token)
  );

  const logs = getLogsFromAlcoholData(data?.alcoholData);

  const getDate = () => {
    if (!data || "error" in data) return;
    const date = new Date(logs[0].data.date);

    return date.toLocaleDateString("cs");
  };

  const apiRequest = useApiRequest();
  const navigate = useNavigate();

  const deleteSession = async () => {
    if (clickedDelete) {
      setClickedDelete(false);
      setDeletionLoading(true);
      const response = await apiRequest.deleteRequest(`/sessions/${data._id}`);
      if (!response) {
        toast.error(
          "Vyskytla se chyba při mazání seance. Zkontroluj připojení k internetu."
        );
      }
      if (response) {
        navigate("/sessions");
      }
      setDeletionLoading(false);
    } else setClickedDelete(true);
  };

  useEffect(() => {
    if (clickedDelete) {
      const timeout = setTimeout(() => {
        setClickedDelete(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [clickedDelete]);

  return (
    <>
      <div className="px-4 relative">
        {data?.userId === user?.id && (
          <div className="flex items-center absolute right-4 gap-2">
            <Button
              size="icon"
              variant={clickedDelete ? "destructive" : "outline"}
              onClick={!deletionLoading ? deleteSession : () => {}}
              disabled={deletionLoading}
            >
              <TrashIcon />
              {deletionLoading && <ReloadIcon />}
            </Button>
            <SessionEditDrawer session={data} mutate={mutate}>
              <Button variant="outline" size="icon">
                <Pencil1Icon />
              </Button>
            </SessionEditDrawer>
          </div>
        )}

        <h2>
          <span
            className="flex items-center gap-2 text-xs text-muted-foreground"
            style={{ lineHeight: "0.75rem" }}
          >
            <span>Seance</span>
            {data?.public && <GlobeIcon />}
          </span>
          {data && "name" in data && (
            <span className="text-lg font-bold">{data?.name}</span>
          )}
        </h2>

        {data && (
          <>
            <div
              className="text-muted-foreground text-sm"
              style={{ lineHeight: "1rem" }}
            >
              {getDate()}
            </div>
            <FriendTag className="pt-1" userId={data?.userId} />

            <div className="py-4">
              <Separator />
            </div>

            <div
              className="text-muted-foreground flex items-center"
              style={{ lineHeight: "1rem" }}
            >
              <span style={{ color: "#efb541" }}>
                <PinTopIcon />
              </span>
              {data.sessionMaxPermille} ‰
            </div>
            <div className="flex items-center gap-2 pt-1">
              {data?.alcoholData?.route?.length > 0 && (
                <MapDrawer
                  asChild
                  logs={getLogsFromAlcoholData(data?.alcoholData)}
                  route={data?.alcoholData?.route}
                >
                  <Button variant="outline" className="items-center gap-2">
                    <Crosshair2Icon />
                    Mapa
                  </Button>
                </MapDrawer>
              )}
              {/* <SessionStatsDrawer data={data} /> */}
            </div>

            <div className="py-4">
              <Separator />
            </div>

            <div className="text-base font-bold">Záznamník</div>
            <div className="pb-4 pt-4">
              <DrinkLog alcoholData={data.alcoholData} />
            </div>
          </>
        )}
      </div>

      {isLoading && <Loading className="pt-4" />}

      {error && !error?.error && !isLoading && (
        <div className="p-4">Vyskytla se chyba</div>
      )}

      {!data && !isLoading && error?.error === "not-found" && (
        <div className="p-4">Seance nebyla nalezena</div>
      )}
    </>
  );
}

// const SessionStatsDrawer = ({ data }: { data: SessionType }) => {
//   return (
//     <StatsDrawer
//       chartData={data.permilleChartData}
//       maxPermille={data.sessionMaxPermille}
//       asChild
//     >
//       <Button variant="outline" className="items-center gap-2">
//         <BarChartIcon />
//         Statistiky
//       </Button>
//     </StatsDrawer>
//   );
// };
